'use client';

/**
 * TranscriptBubble — one turn in the v3 CallScreen's TranscriptRail (design
 * turn 11a). Deliberately NOT MessageBlock — the call transcript is a
 * lighter-weight, caption-style rendering (no tool cards, no retry
 * affordances), matching the Figma "live captions" treatment rather than the
 * full chat surface. It DOES reuse MessageBlock's markdown rendering
 * (MessageResponse/Streamdown) for ALL of Sara's turns — finalized AND live.
 * The live line used to stay plain text with a custom caret span (deliberate
 * while it never carried markdown); with the backend now streaming real
 * markdown through transcript_delta, plain text showed literal ** markers
 * until finalize (FB22) — so the live line now rides the same Streamdown
 * pipeline, whose parseIncompleteMarkdown auto-completes unclosed
 * bold/links mid-stream and whose native block caret replaces the old
 * custom blink span.
 *
 * `live` marks a still-streaming assistant `Message` (`streaming: true`,
 * upserted via appendAssistantDelta — the same pipeline text mode uses;
 * there is no separate in-call model, see transcript-rail.tsx's header) —
 * it turns on Streamdown's caret + isAnimating so the line reads as "still
 * being said". User turns stay plain text (speech-to-text never carries
 * markdown).
 *
 * [Turn 17, 17a/17d/14b] Sizing is responsive at the `sm` boundary (matches
 * the rest of the call surface's mobile/desktop split): below `sm` reads
 * slightly smaller/tighter to fit the phone-width rail; `sm+` is the
 * original desktop scale. The Sara avatar's diameter can't be expressed as
 * a Tailwind class (OrbAvatar's `size` sets inline width/height, which an
 * added className can't override without an `!important` fight this
 * codebase doesn't otherwise use) — so this reads `useIsDesktop`, the same
 * JS matchMedia boundary call-screen.tsx/pinned-card.tsx already share,
 * rather than inventing a second mechanism for one prop.
 */

import type { ReactNode } from 'react';
import { MessageResponse } from '@/components/ai-elements/message';
import {
  OrbAvatar,
  ORB_VARIANT,
} from '@/components/assistant/primitives/orb-avatar';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { cn } from '@/lib/utils';

export interface TranscriptBubbleProps {
  role: 'user' | 'assistant';
  children: ReactNode;
  /** The current in-flight (not-yet-finalized) utterance — renders with
   *  Streamdown's streaming caret. */
  live?: boolean;
  className?: string;
}

export function TranscriptBubble({
  role,
  children,
  live = false,
  className,
}: TranscriptBubbleProps) {
  const isDesktop = useIsDesktop();

  if (role === 'user') {
    return (
      <div
        className={cn(
          // [Turn 17, 17a/14b] radius 15/15/4/15 (was 16/16/4/16); max-w
          // 80% below sm, 82% at sm+ (was a flat 78%); padding 9px/12px
          // below sm, 10px/13px at sm+ (was a flat 10px/14px). Border/bg
          // unchanged per spec.
          'max-w-[80%] self-end break-words rounded-[15px_15px_4px_15px] border-[1px] border-[#efe3d2] bg-white px-[12px] py-[9px] sm:max-w-[82%] sm:px-[13px] sm:py-[10px]',
          className
        )}
      >
        <div className="mb-[2px] text-[10.5px] font-semibold text-[#c0b39d]">
          You
        </div>
        <div className="text-[13px] leading-[1.45] text-[#6b7184] sm:text-[13.5px]">
          {children}
        </div>
      </div>
    );
  }

  // Sara's turns — finalized and live alike — go through the same Streamdown
  // pipeline chat uses (see file header for why the live line joined it in
  // FB22). Typography stays on the wrapping div, same as MessageBlock's
  // approach: MessageResponse carries no font-size opinion, so it inherits —
  // which also makes the live→finalized swap a seamless re-render of
  // identical classes rather than a reflow.
  const content =
    typeof children === 'string' ? (
      <MessageResponse isAnimating={live} caret={live ? 'block' : undefined}>
        {children}
      </MessageResponse>
    ) : (
      children
    );

  return (
    <div className={cn('flex max-w-[86%] gap-2.5', className)}>
      {/* [Turn 17, 17a/17d] 24px below sm, 26px at sm+ (was a flat 28px) —
          see file header for why this branches on useIsDesktop rather than
          a responsive className. */}
      <OrbAvatar
        size={isDesktop ? 26 : 24}
        variant={ORB_VARIANT.VOICE}
        className="shrink-0"
      />
      {/* min-w-0 lets the text shrink inside the flex row (default
          min-width:auto would otherwise force a long unbroken word to the
          orb's right edge and overflow); break-words then wraps it.
          [Turn 17] 13.5px/1.5 below sm, 14px/1.5 at sm+ (was a flat
          14.5px/1.55). */}
      <div
        className={cn(
          'min-w-0 break-words pt-[3px] leading-[1.5] text-[#31364E]',
          isDesktop ? 'text-[14px]' : 'text-[13.5px]'
        )}
      >
        {content}
      </div>
    </div>
  );
}
